// EmailDraftActions — the email-draft affordance row for an EMISSARY client
// communication draft (Page 13). It offers both mailbox providers side by side:
//
//   - Outlook: drafts into the IM's Microsoft 365 mailbox via <EmailDraftButton>
//     + useProviderDraft (always available when Microsoft Entra is configured).
//   - Gmail: when a Gmail account is connected (demo@septima.ai for the demo),
//     "Draft to Gmail" pushes the selected variant into that mailbox's Drafts;
//     when not yet connected it shows "Connect Gmail", linking to the Google
//     OAuth start route so the demo can be set up in one click.
//
// Both paths create a draft only — neither sends. Used by the artefact-body
// EmailBody so an approved/selected EMISSARY draft can land in a real Gmail
// account.

import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import EmailDraftButton from './EmailDraftButton';
import { useProviderDraft } from './use-provider-draft';
import { startGmailConnect, useGmailDraft, useGmailStatus } from './use-gmail';

interface EmailDraftActionsProps {
    /** Recipient address; blank/null creates a draft the IM addresses in-mailbox. */
    to: string | null;
    subject: string;
    body: string;
}

function GmailLogo({ className }: { className?: string }) {
    return <img src="/logos/gmail.svg" alt="" aria-hidden className={className} />;
}

export default function EmailDraftActions({ to, subject, body }: EmailDraftActionsProps) {
    const { draft: draftOutlook, busy: outlookBusy } = useProviderDraft();
    const { draft: draftGmail, busy: gmailBusy } = useGmailDraft();
    const { data: gmailStatus } = useGmailStatus();

    const gmailConfigured = gmailStatus?.configured === true;
    const gmailConnected = gmailStatus?.connected === true;

    // Gmail label tracks connection: connected → push a draft; configured but
    // not connected → start the OAuth dance; unconfigured → omit the button.
    const gmailLabel = gmailConnected
        ? gmailStatus?.email
            ? `Draft to ${gmailStatus.email}`
            : 'Draft to Gmail'
        : 'Connect Gmail';

    const onGmailClick = () => {
        if (gmailConnected) {
            void draftGmail({ to, subject, body });
        } else {
            startGmailConnect();
        }
    };

    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            <EmailDraftButton
                busy={outlookBusy}
                onDraft={() => draftOutlook({ to, subject, body })}
            />
            {gmailConfigured && (
                <button
                    type="button"
                    disabled={gmailBusy}
                    onClick={onGmailClick}
                    title={
                        gmailConnected
                            ? `Push this draft into ${gmailStatus?.email ?? 'the connected Gmail account'}`
                            : 'Connect a Gmail account to push drafts'
                    }
                    className={cn(
                        'inline-flex items-center h-7 px-2.5 gap-1.5 text-annotation rounded-button font-medium',
                        'bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-primary))]',
                        'border border-[rgb(var(--color-divider))]',
                        'hover:bg-[rgb(var(--color-surface-secondary))] hover:border-[rgb(var(--color-text-primary)/0.2)]',
                        'transition-colors duration-card-hover',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)]',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                    )}
                >
                    {gmailBusy ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    ) : (
                        <GmailLogo className="h-3.5 w-3.5" />
                    )}
                    {gmailLabel}
                </button>
            )}
        </div>
    );
}
